'use client';
import { useState } from 'react';
import cn from 'classnames';
import Image from 'next/image';

import Text from '@/components/common/Text/Text';
import Switch from '@/components/common/Switch/Switch';
import { useLoggedInUser } from '@/components/hook/user';
import {
  HANDBAG_CATEGORIES,
  JEWELRY_CATEGORIES
} from '@/const/product_categories';

import CloseIcon from 'public/img/icon/close.svg';
import ChevronDown from 'public/img/icon/chevron-down-black.svg';
import CheckmarkBlack from 'public/img/icon/checkmark-black.svg';

import { State, FilterState } from './ProductListApp';
import FacetMenu from './FacetMenu';
import { createFilterStateCopy } from './dataTools';
import './FilterMenu.style.scss';

type FilterMenuProps = {
  className?: string;
  state: State;
  showTierFilter?: boolean;
  showWishlistFilter?: boolean;
  showDesignerFilter?: boolean;
  onResetFilters: () => void;
  onCloseMenuClick: () => void;
  onFilterChange: (filterState: FilterState) => void;
  /**
   * Facet values pinned by a campaign PLP. Currently only `occasion` is
   * supported: when present, the Occasion group renders ONLY these values,
   * locked and non-removable (occasion values OR together in search, so
   * allowing other selections would broaden results beyond the campaign).
   */
  lockedFacets?: { occasion?: string[] };
};

export type DropdownState = {
  narrowBy: boolean;
  tiers: boolean;
  category: boolean;
  designer: boolean;
  color: boolean;
  material: boolean;
  size: boolean;
  occasion: boolean;
};
type CheckMarkIconProps = {
  className?: string;
};

function CheckMarkIcon(props: CheckMarkIconProps) {
  const { className = '' } = props;
  return (
    <Image
      className={cn('CheckMarkIcon', className)}
      src={CheckmarkBlack}
      height={10}
      width={14}
      alt='Checkmark Icon'
    />
  );
}

function FilterMenu(props: FilterMenuProps) {
  const {
    className = '',
    state,
    showTierFilter = true,
    showWishlistFilter = true,
    showDesignerFilter = true,
    onResetFilters,
    onCloseMenuClick,
    onFilterChange,
    lockedFacets
  } = props;
  const user = useLoggedInUser();

  // On a campaign PLP the Occasion group is restricted to the pinned value(s)
  // only — drop every other occasion so it can't be selected (which would
  // broaden the OR'd occasion filter beyond the campaign). FacetMenu still
  // unions the locked values back in, so a pinned value with no live count
  // remains visible.
  const lockedOccasions = lockedFacets?.occasion;
  const occasionFacets =
    lockedOccasions && lockedOccasions.length > 0
      ? Object.fromEntries(
          Object.entries(state.facets['occasion'] ?? {}).filter(([value]) =>
            lockedOccasions.includes(value)
          )
        )
      : state.facets['occasion'];
  const [dropdownState, setDropdownState] = useState<DropdownState>({
    narrowBy: false,
    tiers: false,
    category: false,
    designer: false,
    color: false,
    material: false,
    size: false,
    occasion: false
  });

  const handleToggleDropdown = (dropdown: keyof DropdownState) => {
    setDropdownState({
      ...dropdownState,
      [dropdown]: !dropdownState[dropdown]
    });
  };

  const getCategoryContent = () => {
    const collectionType = state.filterState.collection.collectionType[0];

    if (collectionType === 'Handbags') {
      return HANDBAG_CATEGORIES;
    }

    // If it isn't a handbag, it's jewelry
    return JEWELRY_CATEGORIES;
  };

  const getNarrowByTitle = () => {
    let title = 'Narrow By';
    let count = 0;
    if (state.filterState.newArrivals) {
      count++;
    }
    if (showWishlistFilter && state.filterState.wishlist) {
      count++;
    }
    if (count > 0) {
      title += ` (${count})`;
    }
    return title;
  };

  const getFilterTitle = (
    filterType:
      | 'brand'
      | 'color'
      | 'occasion'
      | 'material'
      | 'subscriptionLevel'
  ) => {
    let title = '';
    switch (filterType) {
      case 'color':
        title = 'Color';
        break;
      case 'brand':
        title = 'Designer';
        break;
      case 'material':
        title = 'Material';
        break;
      case 'occasion':
        title = 'Occasion';
        break;
      case 'subscriptionLevel':
        title = 'Tiers';
    }

    const filters = state.filterState[filterType];

    if (filters && filters.length > 0) {
      title += ` (${filters.length})`;
    }

    return title;
  };

  const filterBtnCopy = () => {
    if (!state.totalFilters) {
      return 'Show results';
    } else if (state.totalResults === 0) {
      return 'No Results found';
    } else {
      return `Show ${state.totalResults} results`;
    }
  };

  return (
    <div className={cn('FilterMenu', className)}>
      <div className='FilterMenu-content-wrapper'>
        <div className='FilterMenu-top-wrapper'>
          <div className='menu-row reset-row menu-top-bar'>
            <Text
              className='reset-text cursor'
              onClick={onResetFilters}
            >
              Reset
            </Text>
            <Image
              className='close-button cursor'
              src={CloseIcon}
              height={20}
              width={20}
              alt='Close Filter Menu Button'
              onClick={onCloseMenuClick}
            />
          </div>
          <div className='menu-row available-row'>
            <Text
              className='menu-label'
              uppercase
            >
              Available Now
            </Text>
            <Switch
              checked={state.filterState.inStock}
              onChange={(value) => {
                const newState = createFilterStateCopy(state.filterState);
                newState.inStock = value;
                newState.pageNo = 1;
                onFilterChange(newState);
              }}
            />
          </div>
          <div className='FacetMenu menu-row facet-row narrowBy-row'>
            <div
              className='facet-row-label cursor'
              role='button'
              onClick={() => {
                handleToggleDropdown('narrowBy');
              }}
            >
              <Text
                className='menu-label'
                uppercase
              >
                {getNarrowByTitle()}
              </Text>
              <Image
                className={cn('chevron-down-icon cursor', {
                  flip: dropdownState.narrowBy
                })}
                src={ChevronDown}
                height={16}
                width={16}
                alt='Narrow By Filter'
              />
            </div>
            <div
              className={cn('facet-row-content', {
                open: dropdownState.narrowBy
              })}
            >
              <div className='facet-row-item cursor'>
                <Text
                  onClick={() => {
                    const filterCopy = createFilterStateCopy(state.filterState);
                    filterCopy.newArrivals = !filterCopy.newArrivals;
                    filterCopy.pageNo = 1;
                    onFilterChange(filterCopy);
                  }}
                >
                  New Arrivals
                </Text>
                {state.filterState.newArrivals && (
                  <CheckMarkIcon className='' />
                )}
              </div>

              {showWishlistFilter && user && (
                <div className='facet-row-item cursor'>
                  <Text
                    onClick={() => {
                      const filterCopy = createFilterStateCopy(
                        state.filterState
                      );
                      filterCopy.wishlist = !filterCopy.wishlist;
                      // Reset to page 1
                      filterCopy.pageNo = 1;
                      onFilterChange(filterCopy);
                    }}
                  >
                    My Wishlist
                  </Text>
                  {state.filterState.wishlist && <CheckMarkIcon className='' />}
                </div>
              )}
            </div>
          </div>
          {showTierFilter && (
            <div className='FacetMenu menu-row facet-row tier-row'>
              <div
                className='facet-row-label cursor'
                role='button'
                onClick={() => {
                  handleToggleDropdown('tiers');
                }}
              >
                <Text
                  className='menu-label'
                  uppercase
                >
                  {getFilterTitle('subscriptionLevel')}
                </Text>
                <Image
                  className={cn('chevron-down-icon cursor', {
                    flip: dropdownState.tiers
                  })}
                  src={ChevronDown}
                  height={16}
                  width={16}
                  alt='Tiers Filter'
                />
              </div>
              <div
                className={cn('facet-row-content', {
                  open: dropdownState.tiers
                })}
              >
                <div className='facet-row-item cursor'>
                  <Text
                    onClick={() => {
                      const filterCopy = createFilterStateCopy(
                        state.filterState
                      );
                      filterCopy.subscriptionLevel =
                        filterCopy.subscriptionLevel.includes('Couture')
                          ? filterCopy.subscriptionLevel.filter(
                              (v) => v !== 'Couture'
                            )
                          : [...filterCopy.subscriptionLevel, 'Couture'];
                      filterCopy.pageNo = 1;
                      onFilterChange(filterCopy);
                    }}
                  >
                    Couture
                  </Text>
                  {state.filterState.subscriptionLevel.includes('Couture') && (
                    <CheckMarkIcon className='' />
                  )}
                </div>
                <div className='facet-row-item cursor'>
                  <Text
                    onClick={() => {
                      const filterCopy = createFilterStateCopy(
                        state.filterState
                      );
                      filterCopy.subscriptionLevel =
                        filterCopy.subscriptionLevel.includes('Classique')
                          ? filterCopy.subscriptionLevel.filter(
                              (v) => v !== 'Classique'
                            )
                          : [...filterCopy.subscriptionLevel, 'Classique'];

                      onFilterChange(filterCopy);
                    }}
                  >
                    Classique
                  </Text>
                  {state.filterState.subscriptionLevel.includes(
                    'Classique'
                  ) && <CheckMarkIcon className='' />}
                </div>
                <div className='facet-row-item cursor'>
                  <Text
                    onClick={() => {
                      const filterCopy = createFilterStateCopy(
                        state.filterState
                      );
                      filterCopy.subscriptionLevel =
                        filterCopy.subscriptionLevel.includes('Premier')
                          ? filterCopy.subscriptionLevel.filter(
                              (v) => v !== 'Premier'
                            )
                          : [...filterCopy.subscriptionLevel, 'Premier'];
                      filterCopy.pageNo = 1;
                      onFilterChange(filterCopy);
                    }}
                  >
                    Premier
                  </Text>
                  {state.filterState.subscriptionLevel.includes('Premier') && (
                    <CheckMarkIcon />
                  )}
                </div>
              </div>
            </div>
          )}
          {/* Category filter is only shown if the user is not in "all" */}
          {state.filterState.collection.collectionType.length > 0 && (
            <div className='FacetMenu menu-row facet-row category-row'>
              <div
                className='facet-row-label cursor'
                role='button'
                onClick={() => {
                  handleToggleDropdown('category');
                }}
              >
                <Text
                  className='menu-label'
                  uppercase
                >
                  Category
                  {state.filterState.collection.types.length > 0 &&
                    ` (${state.filterState.collection.types.length})`}
                </Text>
                <Image
                  className={cn('chevron-down-icon cursor', {
                    flip: dropdownState.category
                  })}
                  src={ChevronDown}
                  height={16}
                  width={16}
                  alt='Categories Filter'
                />
              </div>
              <div
                className={cn('facet-row-content', {
                  open: dropdownState.category
                })}
              >
                {getCategoryContent().map((category) => {
                  return (
                    <div
                      key={category}
                      className='facet-row-item cursor'
                    >
                      <Text
                        onClick={() => {
                          const filterCopy = createFilterStateCopy(
                            state.filterState
                          );
                          filterCopy.collection.types =
                            filterCopy.collection.types.includes(category)
                              ? filterCopy.collection.types.filter(
                                  (c) => c !== category
                                )
                              : [...filterCopy.collection.types, category];
                          onFilterChange(filterCopy);
                        }}
                      >
                        {category}
                      </Text>
                      {state.filterState.collection.types.includes(
                        category
                      ) && <CheckMarkIcon className='' />}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {showDesignerFilter && (
            <FacetMenu
              className='menu-row facet-row designer-row'
              title={getFilterTitle('brand')}
              filterState={state.filterState}
              dropdownStateKey='designer'
              stateKey='brand'
              facets={state.facets['brand']}
              open={dropdownState.designer}
              onFacetClick={(newFilterState) => onFilterChange(newFilterState)}
              onFilterOpen={(value) => {
                handleToggleDropdown(value);
              }}
              checkmarkComponent={<CheckMarkIcon />}
            />
          )}
          <FacetMenu
            className='menu-row facet-row color-row'
            title={getFilterTitle('color')}
            filterState={state.filterState}
            dropdownStateKey='color'
            stateKey='color'
            facets={state.facets['color']}
            open={dropdownState.color}
            onFacetClick={(newFilterState) => onFilterChange(newFilterState)}
            onFilterOpen={(value) => {
              handleToggleDropdown(value);
            }}
            checkmarkComponent={<CheckMarkIcon />}
          />
          <FacetMenu
            className='menu-row facet-row material-row'
            title={getFilterTitle('material')}
            filterState={state.filterState}
            dropdownStateKey='material'
            stateKey='material'
            facets={state.facets['material']}
            open={dropdownState.material}
            onFacetClick={(newFilterState) => onFilterChange(newFilterState)}
            onFilterOpen={(value) => {
              handleToggleDropdown(value);
            }}
            checkmarkComponent={<CheckMarkIcon />}
          />
          <FacetMenu
            className='menu-row facet-row occasion-row'
            title={getFilterTitle('occasion')}
            filterState={state.filterState}
            dropdownStateKey='occasion'
            stateKey='occasion'
            facets={occasionFacets}
            lockedValues={lockedOccasions}
            open={dropdownState.occasion}
            onFacetClick={(newFilterState) => onFilterChange(newFilterState)}
            onFilterOpen={(value) => {
              handleToggleDropdown(value);
            }}
            checkmarkComponent={<CheckMarkIcon />}
          />
        </div>
        <button
          className={cn('sticky-button cursor', {
            disable: state.loading || state.totalResults === 0
          })}
          onClick={onCloseMenuClick}
        >
          {filterBtnCopy()}
        </button>
      </div>
    </div>
  );
}

export default FilterMenu;
